import React, { useState, useRef, useContext, useEffect } from "react";
import { Upload, CheckCircle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { SurveyContext } from "../context/surveyContext";
import { SessionContext } from "../context/sessionContext";
import { useUser } from "../context/UserContext";
import "react-toastify/dist/ReactToastify.css";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SurveyFlowHeader from "../components/SurveyFlowHeader";

const MAX_FILE_SIZE = 10 * 1024 * 1024;


const RESTRICTED_PLANS = ["Freemium", "Custom"];
const MIN_QUESTION_COUNT = 20;
const MAX_QUESTION_COUNT = 40;

const CreateSurvey = () => {
  const { setSurveyData, setSurveyMeta } = useContext(SurveyContext);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);
  const [fileName, setFileName] = useState("");
  const { isAuthenticated } = useContext(SessionContext);
  const { userData } = useUser();
  const planName = userData?.planDetails?.plan_name;
  const isRestrictedPlan = RESTRICTED_PLANS.includes(planName);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const isAbandonableUploadFlow = isAuthenticated && (isUploading || isUploaded);
  const [showLeaveWarning, setShowLeaveWarning] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    if (!isAbandonableUploadFlow) return undefined;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isAbandonableUploadFlow]);

  const handleLogoClick = (e) => {
    if (!isAbandonableUploadFlow) return;
    e.preventDefault();
    setPendingNavigation("/");
    setShowLeaveWarning(true);
  };

  const handleConfirmLeave = () => {
    setShowLeaveWarning(false);
    if (pendingNavigation) navigate(pendingNavigation);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isUploading && !isUploaded) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (!isUploading && !isUploaded) {
      const files = e.dataTransfer.files;
      if (files.length > 0) validateAndUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && !isUploading && !isUploaded) validateAndUpload(file);
  };

  const validateAndUpload = async (file) => {
    setIsUploading(true);
    setFileName(file.name);

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File size exceeds 10 MB. Please upload a smaller file.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setIsUploading(false);
      return;
    }

    try {
      const formDataObj = new FormData();
      formDataObj.append("file", file);
      const validateRes = await fetch(
        `${process.env.REACT_APP_API_URL}/auth/checkAndValidateFiles`,
        {
          method: "POST",
          body: formDataObj,
        }
      );

      const validateData = await validateRes.json();

      const isValid =
        validateData &&
        validateData.questionCount !== undefined &&
        validateData.questionCountConfidence !== undefined &&
        validateData.surveyTheme !== undefined;

      if (!isValid) {
        toast.error("This is not a valid survey file.");
        if (fileInputRef.current) fileInputRef.current.value = "";
        setIsUploading(false);
        return;
      }

      if (isRestrictedPlan) {
        const { questionCount } = validateData;
        if (questionCount < MIN_QUESTION_COUNT) {
          toast.error(
            `Your question count is less than ${MIN_QUESTION_COUNT}. For your plan, question count needs to be between ${MIN_QUESTION_COUNT} and ${MAX_QUESTION_COUNT}.`,
          );
          if (fileInputRef.current) fileInputRef.current.value = "";
          setIsUploading(false);
          return;
        }
        if (questionCount > MAX_QUESTION_COUNT) {
          toast.error(
            `Your question count is more than ${MAX_QUESTION_COUNT}. For your plan, question count needs to be between ${MIN_QUESTION_COUNT} and ${MAX_QUESTION_COUNT}.`,
          );
          if (fileInputRef.current) fileInputRef.current.value = "";
          setIsUploading(false);
          return;
        }
      }

      const meta = {
        questionCount: validateData.questionCount,
        questionCountConfidence: validateData.questionCountConfidence,
        surveyTheme: validateData.surveyTheme,
      };

      setSurveyMeta(meta);
      localStorage.setItem("surveyMeta", JSON.stringify(meta));

      setIsUploading(false);
      setIsUploaded(true);
      handleFileUpload(file);
    } catch (err) {
      console.error("Validation error:", err);
      toast.error("This is not a valid survey file.");
      setIsUploading(false);
    }
  };

  const handleFileUpload = (file) => {
    setFileName(file.name);
    setSurveyData(file);
  };

  const openFileDialog = () => {
    if (!isUploading && !isUploaded) fileInputRef.current?.click();
  };

  const handleStartResearch = () => navigate("/define-audience");

  const handleStartOver = () => {
    setIsUploaded(false);
    setIsUploading(false);
    setFileName("");
    setSurveyMeta(null);
    setSurveyData(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const getDropZoneClasses = () => {
    let classes = "upload-survey-upload-zone";
    if (isDragOver) classes += " upload-survey-upload-zone--active";
    if (isUploading) classes += " upload-survey-upload-zone--uploading";
    if (isUploaded) classes += " upload-survey-upload-zone--uploaded";
    return classes;
  };

  const renderUploadContent = () => {
    if (isUploaded) {
      return (
        <div className="upload-survey-upload-zone__content">
          <div className="upload-survey-upload-zone__icon upload-survey-upload-zone__icon--success">
            <CheckCircle size={24} />
          </div>
          <h3 className="upload-survey-upload-zone__title">
            Survey Uploaded Successfully
          </h3>
          <p className="upload-survey-upload-zone__filename">{fileName}</p>

          <div className="upload-survey-upload-zone__actions">
            <button
              className="upload-survey-btn upload-survey-btn--primary"
              onClick={handleStartResearch}
            >
              Start Setting Up Your Research
            </button>
            <button
              className="upload-survey-btn upload-survey-btn--secondary"
              onClick={handleStartOver}
            >
              Start Over
            </button>
          </div>
        </div>
      );
    }

    if (isUploading) {
      return (
        <div className="upload-survey-upload-zone__content">
          <div className="upload-survey-upload-zone__icon">
            <div className="upload-survey-loading-dots" aria-hidden="true">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
          <h3 className="upload-survey-upload-zone__title">
            Uploading your file...
          </h3>
          <p className="upload-survey-upload-zone__filename">{fileName}</p>
        </div>
      );
    }

    return (
      <div className="upload-survey-upload-zone__content">
        <div className="upload-survey-upload-zone__icon">
          <Upload size={24} />
        </div>
        <h3 className="upload-survey-upload-zone__title">
          Upload your survey file
        </h3>
        <div className="upload-survey-upload-zone__description">
          <p>Accepted formats are PDF and DOCX</p>
          <p>Maximum limit is 10 MB.</p>
          {isRestrictedPlan && (
            <p>
              Your plan requires a survey with {MIN_QUESTION_COUNT}–{MAX_QUESTION_COUNT} questions.
            </p>
          )}
        </div>

        <button className="upload-survey-btn upload-survey-btn--browse">
          Browse Files
        </button>
      </div>
    );
  };

  return (
    <div className="page-bg">
       <SurveyFlowHeader
        flow="uploaded"
        activeStep="upload"
        isAuthenticated={isAuthenticated}
        maxStepReached="upload"
        onLogoClick={handleLogoClick}
        rightSlot={
          isAuthenticated && (
            <Link to="/projects">
              <button className="navbar-btn-secondary">Back to Projects →</button>
            </Link>
          )
        }
      />
      <div className="content-center">
        <div className="upload-survey-container">
          <h1 className="upload-survey-title">Start Your Project</h1>
          <div
            className={getDropZoneClasses()}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={!isUploading && !isUploaded ? openFileDialog : undefined}
          >
            {renderUploadContent()}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx"
            onChange={handleFileSelect}
            className="upload-survey-file-input"
          />
        </div>
        <div className="create-survey-section">
          <h3 className="create-survey-title">
            Don’t have a survey?
          </h3>
          <p className="create-survey-description">
            Create one from scratch in minutes using our built-in survey builder,
            no research experience needed.
          </p>
          <button
            className="create-survey-btn create-survey-btn--secondary"
            onClick={() => navigate("/add-survey")}
          >
            Create Survey
          </button>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={3000} />

      <Dialog
        open={showLeaveWarning}
        onClose={() => setShowLeaveWarning(false)}
        sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(0, 0, 0, 0.8)" } }}
        PaperProps={{
          sx: {
            backgroundColor: "#000000",
            border: "2px solid #3b82f6",
            borderRadius: "8px",
            color: "white",
            width: "500px",
            maxWidth: "90vw",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "#000000",
            color: "white",
            textAlign: "center",
            fontSize: "1.3rem",
            fontWeight: "bold",
            position: "relative",
          }}
        >
          Leave without finishing?
          <IconButton
            aria-label="close"
            onClick={() => setShowLeaveWarning(false)}
            sx={{ position: "absolute", right: 8, top: 8, color: "#3b82f6" }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "#000000", color: "white", textAlign: "center", padding: "15px 20px" }}>
          <div>Uploaded surveys aren't saved until you finish this flow.</div>
          <div style={{ marginTop: "12px" }}>
            If you leave now, all progress will be lost and you'll need to start over.
          </div>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "#000000", justifyContent: "center", paddingBottom: "15px", gap: "12px" }}>
          <Button
            onClick={() => setShowLeaveWarning(false)}
            sx={{ color: "#ffffff", border: "1px solid #ffffff", padding: "10px 26px" }}
          >
            Stay and Finish
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmLeave}
            sx={{ backgroundColor: "#3b82f6", color: "white", padding: "10px 26px" }}
          >
            Leave Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CreateSurvey;